import { z } from 'zod/v4';
import {
  APARTMENT_SUB_TYPES,
  type BigDecimalRangeDto,
  BusinessArea,
  convertBoundingBoxParamValueToMultiPolygon,
  type CoordinateDto,
  Currency,
  type FallbackSearch,
  HOUSE_SUB_TYPES,
  LAND_SUB_TYPES,
  Language,
  LifestyleCategory,
  ListingSortOption,
  ListingStatus,
  MarketCountryCode,
  MeasurementSystem,
  type MultiPolygonG2D,
  PropertyMarketingType,
  PropertySubType,
  PropertyType,
  type SearchFilterRequestPropertyTypeSubType,
  type SearchListingsFilters,
  type SearchListingsOptions,
  mapDeprecatedPropertyTypeParams,
} from '@ev-tech/search-modules-api';

import { parseMultiPolygonFromUrlString } from './multiPolygonUrlParam';
import { type AdditionalUiStateParams } from '../types/types';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';
import { SortingOptionId } from '../enums/enums';
import { SORT_BY_OPTIONS } from '../consts/consts';

const DEPRECATED_FIELDS: { deprecatedKey: string; newKey: string; transform?: (value: unknown) => unknown }[] = [
  { deprecatedKey: 'marketingType', newKey: 'propertyMarketingType' }, // NS-6759
  { deprecatedKey: 'boundingBox', newKey: 'multiPolygon', transform: convertBoundingBoxParamValueToMultiPolygon },
];

export type ParsedSearchParams<T = Record<string, unknown>, TRequiredFields extends boolean = false> = {
  options: TRequiredFields extends true ? SearchListingsOptions : Partial<SearchListingsOptions>;
  filters: TRequiredFields extends true ? SearchListingsFilters : Partial<SearchListingsFilters>;
  additionalParams: Partial<T & AdditionalUiStateParams>;
};

export const SECURITY_LIMITS = {
  MAX_NESTING_DEPTH: 5,
  MAX_QUERY_STRING_LENGTH: 10000,
} as const;

/**
 * Ensures that only the new keys are present in the filters and old keys are still supported
 * @param params - filters and options
 * @returns filters and options with deprecated keys replaced
 */
export const replaceDeprecatedParams = (params: Record<string, unknown>) => {
  const refinedParams = { ...params };

  for (const { deprecatedKey, newKey, transform } of DEPRECATED_FIELDS) {
    const hasDeprecated = deprecatedKey in refinedParams;
    const hasReplacement = newKey in refinedParams;

    if (hasDeprecated && hasReplacement) {
      console.warn(`Both ${deprecatedKey} and ${newKey} are present in the filters. Please use only ${newKey}.`);
      // Reason: multiPolygon (e.g. freedraw) must not be replaced by a converted boundingBox rectangle.
      if (deprecatedKey === 'boundingBox') {
        delete refinedParams[deprecatedKey];
        continue;
      }
    }

    if (hasDeprecated) {
      refinedParams[newKey] = transform?.(refinedParams[deprecatedKey]) ?? refinedParams[deprecatedKey];
      delete refinedParams[deprecatedKey];
    }
  }

  if (refinedParams.propertyType || refinedParams.propertySubType) {
    // Ensure propertyType and propertySubType are arrays before mapping
    const propertyTypeArray = refinedParams.propertyType
      ? Array.isArray(refinedParams.propertyType)
        ? refinedParams.propertyType
        : [refinedParams.propertyType]
      : undefined;
    const propertySubTypeArray = refinedParams.propertySubType
      ? Array.isArray(refinedParams.propertySubType)
        ? refinedParams.propertySubType
        : [refinedParams.propertySubType]
      : undefined;

    refinedParams.propertyTypeSubType = mapDeprecatedPropertyTypeParams(
      propertyTypeArray as PropertyType[],
      propertySubTypeArray as PropertySubType[],
    );
    delete refinedParams.propertyType;
    delete refinedParams.propertySubType;
  }

  if ('placeId' in refinedParams) {
    if ('placeIds' in refinedParams) {
      console.warn('Both placeId and placeIds are present in the filters. Please use only placeIds.');
    } else {
      const placeId = refinedParams.placeId;
      refinedParams.placeIds = Array.isArray(placeId) ? placeId : [placeId];
    }

    delete refinedParams.placeId;
  }

  return refinedParams;
};

export const simplifyPropertyTypeSubType = (
  propertyTypeSubType: SearchFilterRequestPropertyTypeSubType,
): SearchFilterRequestPropertyTypeSubType => {
  const result = propertyTypeSubType;
  if (propertyTypeSubType.house?.length === HOUSE_SUB_TYPES.length) {
    result.house = [];
  }
  if (propertyTypeSubType.apartment?.length === APARTMENT_SUB_TYPES.length) {
    result.apartment = [];
  }
  if (propertyTypeSubType.land?.length === LAND_SUB_TYPES.length) {
    result.land = [];
  }
  return result;
};

export const ensureCompatibilityWithOldParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...params };

  if (params.propertyType) {
    result.propertyType = Array.isArray(params.propertyType) ? params.propertyType : [params.propertyType];
  }

  if (params.propertySubType) {
    result.propertySubType = Array.isArray(params.propertySubType) ? params.propertySubType : [params.propertySubType];
  }

  return result;
};

// #region Zod schemas

const rangeFilterSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
}) satisfies z.ZodType<BigDecimalRangeDto>;

const fallbackSearchSchema = z.object({
  activateUnderEquals: z.number(),
  newRadius: z.number(),
  propertyCount: z.number(),
  radiusAroundShops: z.boolean().optional(),
  disableShopFilter: z.boolean().optional(),
}) satisfies z.ZodType<FallbackSearch>;

const coordinateSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  strategy: z.string().optional(),
}) satisfies z.ZodType<CoordinateDto>;

const multiPolygonSchema = z.object({
  type: z.string().refine((v) => v === 'MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.array(z.number())))),
}) satisfies z.ZodType<MultiPolygonG2D>;

const filtersSchema = z.object({
  price: rangeFilterSchema.optional(),
  livingSurface: rangeFilterSchema.optional(),
  plotSurface: rangeFilterSchema.optional(),
  totalSurface: rangeFilterSchema.optional(),
  agentId: z.string().optional(),
  promotedBy: z.string().optional(),
  bathrooms: rangeFilterSchema.optional(),
  bedrooms: rangeFilterSchema.optional(),
  businessArea: z.array(z.string().toLowerCase().pipe(z.enum(BusinessArea))).optional(),
  constructionYear: rangeFilterSchema.optional(),
  excludedPropertyIds: z.array(z.string()).optional(),
  fallbackSearch: fallbackSearchSchema.optional(),
  hasAirConditioning: z.boolean().optional(),
  hasBalcony: z.boolean().optional(),
  hasBasement: z.boolean().optional(),
  hasBuiltInKitchen: z.boolean().optional(),
  hasDoorman: z.boolean().optional(),
  hasElevator: z.boolean().optional(),
  hasFloorplan: z.boolean().optional(),
  hasGarage: z.boolean().optional(),
  hasGarden: z.boolean().optional(),
  hasHelipad: z.boolean().optional(),
  hasPatio: z.boolean().optional(),
  hasPriceUpdated: z.boolean().optional(),
  hasSeaOrLakeView: z.boolean().optional(),
  hasSecuritySystem: z.boolean().optional(),
  hasSwimmingPool: z.boolean().optional(),
  hasTennisCourt: z.boolean().optional(),
  hasTerrace: z.boolean().optional(),
  hasVideoNormalized: z.boolean().optional(),
  hasVtour: z.boolean().optional(),
  hasVtourNormalized: z.boolean().optional(),
  hasWaterfront: z.boolean().optional(),
  listingIds: z.array(z.string()).optional(),
  propertyMarketingType: z.array(z.string().toLowerCase().pipe(z.enum(PropertyMarketingType))).optional(),
  masterDataLiPaIds: z.array(z.string()).optional(),
  masterDataShopIds: z.array(z.string()).optional(),
  parentId: z.string().optional(),
  placeIds: z.array(z.string()).optional(),
  postalCode: z.string().optional(),
  priceOnRequest: z.boolean().optional(),
  propertyTypeSubType: z
    .partialRecord(z.string().toLowerCase().pipe(z.enum(PropertyType)), z.array(z.string().toLowerCase().pipe(z.enum(PropertySubType))))
    .optional(),
  rooms: rangeFilterSchema.optional(),
  searchRadius: z.number().optional(),
  shopIds: z.array(z.string()).optional(),
  since: z.string().optional(),
  sortByDistanceToPoint: coordinateSchema.optional(),
  sortingOptions: z.array(z.string().toUpperCase().pipe(z.enum(ListingSortOption))).optional(),
  statuses: z.array(z.string().toUpperCase().pipe(z.enum(ListingStatus))).optional(),
  lifestyleCategory: z.array(z.string().toUpperCase().pipe(z.enum(LifestyleCategory))).optional(),
  rentNetAed: rangeFilterSchema.optional(),
  rentNetCad: rangeFilterSchema.optional(),
  rentNetChf: rangeFilterSchema.optional(),
  rentNetClf: rangeFilterSchema.optional(),
  rentNetClp: rangeFilterSchema.optional(),
  rentNetCny: rangeFilterSchema.optional(),
  rentNetCop: rangeFilterSchema.optional(),
  rentNetCzk: rangeFilterSchema.optional(),
  rentNetDkk: rangeFilterSchema.optional(),
  rentNetEur: rangeFilterSchema.optional(),
  rentNetGbp: rangeFilterSchema.optional(),
  rentNetHkd: rangeFilterSchema.optional(),
  rentNetHuf: rangeFilterSchema.optional(),
  rentNetMur: rangeFilterSchema.optional(),
  rentNetOmr: rangeFilterSchema.optional(),
  rentNetPerSquareMeterAed: rangeFilterSchema.optional(),
  rentNetPerSquareMeterCad: rangeFilterSchema.optional(),
  rentNetPerSquareMeterChf: rangeFilterSchema.optional(),
  rentNetPerSquareMeterClf: rangeFilterSchema.optional(),
  rentNetPerSquareMeterClp: rangeFilterSchema.optional(),
  rentNetPerSquareMeterCny: rangeFilterSchema.optional(),
  rentNetPerSquareMeterCop: rangeFilterSchema.optional(),
  rentNetPerSquareMeterCzk: rangeFilterSchema.optional(),
  rentNetPerSquareMeterDkk: rangeFilterSchema.optional(),
  rentNetPerSquareMeterEur: rangeFilterSchema.optional(),
  rentNetPerSquareMeterGbp: rangeFilterSchema.optional(),
  rentNetPerSquareMeterHkd: rangeFilterSchema.optional(),
  rentNetPerSquareMeterHuf: rangeFilterSchema.optional(),
  rentNetPerSquareMeterMur: rangeFilterSchema.optional(),
  rentNetPerSquareMeterOmr: rangeFilterSchema.optional(),
  rentNetPerSquareMeterQar: rangeFilterSchema.optional(),
  rentNetPerSquareMeterSek: rangeFilterSchema.optional(),
  rentNetPerSquareMeterTry: rangeFilterSchema.optional(),
  rentNetPerSquareMeterUsd: rangeFilterSchema.optional(),
  rentNetPerSquareMeterZar: rangeFilterSchema.optional(),
  rentNetQar: rangeFilterSchema.optional(),
  rentNetSek: rangeFilterSchema.optional(),
  rentNetTry: rangeFilterSchema.optional(),
  rentNetUsd: rangeFilterSchema.optional(),
  rentNetZar: rangeFilterSchema.optional(),
  preferences: z.record(z.string(), z.number()).optional(),
  multiPolygon: z
    .preprocess((value) => (typeof value === 'string' ? parseMultiPolygonFromUrlString(value) : value), multiPolygonSchema.optional())
    .optional(),
});

const optionsSchema = z.object({
  currency: z.string().toUpperCase().pipe(z.enum(Currency)).optional(),
  language: z.string().toLowerCase().pipe(z.enum(Language)).optional(),
  measurementSystem: z.string().toLowerCase().pipe(z.enum(MeasurementSystem)).optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().optional(),
  marketCountryCode: z.string().toUpperCase().pipe(z.enum(MarketCountryCode)).optional(),
});

// #endregion

export function parseParams<T extends Record<string, unknown>>(params: Partial<T>): ParsedSearchParams<T, false> {
  const compatibleParams = ensureCompatibilityWithOldParams(params);
  const refinedParams = replaceDeprecatedParams(compatibleParams);
  const filtersResult = filtersSchema.safeParse(refinedParams);
  const optionsResult = optionsSchema.safeParse(refinedParams);

  let filters: Partial<SearchListingsFilters> = {};
  let options: Partial<SearchListingsOptions> = {};

  if (filtersResult.success) {
    filters = filtersResult.data;
  } else {
    console.warn(`Error while parsing filters from URL params: ${z.treeifyError(filtersResult.error).errors.join(', ')}`);
  }

  if (optionsResult.success) {
    options = optionsResult.data;
  } else {
    console.warn(`Error while parsing options from URL params: ${z.treeifyError(optionsResult.error).errors.join(', ')}`);
  }
  // Get additional parameters by removing known filters and options
  const knownKeys = new Set([...Object.keys(filtersSchema.shape), ...DEPRECATED_FIELDS, ...Object.keys(optionsSchema.shape)]);
  const additionalParams = Object.fromEntries(Object.entries(refinedParams).filter(([key]) => !knownKeys.has(key))) as Partial<
    T & AdditionalUiStateParams
  >;

  if (filters.propertyTypeSubType) {
    filters.propertyTypeSubType = simplifyPropertyTypeSubType(filters.propertyTypeSubType);
  }

  if (filters.sortingOptions?.[0] === SortingOptionId.PREFERENCES) {
    // Reason: additionalParams is Partial<T & AdditionalUiStateParams>; assigning SortingOptionId
    // directly fails because TS intersects it with T["sortingOptionId"].
    (additionalParams as AdditionalUiStateParams).sortingOptionId =
      additionalParams.searchMode === SearchMode.AI ? SortingOptionId.PREFERENCES : SortingOptionId.SMART_SORT;
  }

  if (additionalParams.sortingOptionId) {
    const sortingOptionIdResult = z.enum(SortingOptionId).safeParse(additionalParams.sortingOptionId);
    if (sortingOptionIdResult.success) {
      const sortingOption = SORT_BY_OPTIONS.find((option) => option.id === sortingOptionIdResult.data)?.sortingOption;
      if (sortingOption) {
        filters.sortingOptions = [sortingOption];
      }
    } else {
      delete additionalParams.sortingOptionId;
    }
  }

  if (additionalParams.placeName != null) {
    // Reason: qs.parse with parseNumbers coerces numeric-looking place names
    // (e.g. postal codes like "20354") into numbers. Keep them as strings for the UI.
    // Boolean values from parseBooleans (e.g. placeName=true) are rejected as invalid.
    const placeNameResult = z.union([z.string(), z.number()]).transform(String).safeParse(additionalParams.placeName);
    if (placeNameResult.success) {
      (additionalParams as AdditionalUiStateParams).placeName = placeNameResult.data;
    } else {
      delete additionalParams.placeName;
    }
  }

  return { filters, options, additionalParams };
}

export function validateLanguage(language: unknown): language is Language {
  return z.enum(Language).safeParse(language).success;
}

export function validateMarketCountryCode(countryCode: unknown): countryCode is MarketCountryCode {
  return z.enum(MarketCountryCode).safeParse(countryCode).success;
}

/**
 * Normalizes a string to match MarketCountryCode values, handling the case where
 * 'private-office' is lowercase while all other codes are uppercase.
 */
export function normalizeMarketCountryCode(value: string): string {
  if (value.toLowerCase() === 'private-office') {
    return 'private-office';
  }
  return value.toUpperCase();
}
